class LioraLens extends HTMLElement {
  connectedCallback() {
    this.bubble = this.querySelector('[data-liora-bubble]');
    this.panel = this.querySelector('[data-liora-panel]');
    this.video = this.querySelector('[data-liora-video]');
    this.preview = this.querySelector('[data-liora-preview]');
    this.closeButton = this.querySelector('[data-liora-close]');
    this.soundButton = this.querySelector('[data-liora-sound]');
    this.playButton = this.querySelector('[data-liora-play]');
    this.inlineButton = this.querySelector('[data-liora-inline]');
    this.inlinePreview = this.querySelector('[data-liora-inline-preview]');
    this.shopColor = this.querySelector('[data-liora-shop-color]');
    this.colorFeedAbortController = new AbortController();

    this.bubble?.addEventListener('click', () => this.open(this.bubble));
    this.inlineButton?.addEventListener('click', () => this.open(this.inlineButton));
    this.closeButton?.addEventListener('click', () => this.close());
    this.soundButton?.addEventListener('click', () => this.toggleSound());
    this.playButton?.addEventListener('click', () => this.togglePlay());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    // Eurus can rebuild the product information after this section first loads.
    // Place the product features now, then retry after the product scripts settle.
    this.placeProductFeatures();
    [250, 700, 1500, 3000].forEach((delay) => {
      window.setTimeout(() => this.placeProductFeatures(), delay);
    });

    this.productObserver = new MutationObserver(() => {
      window.clearTimeout(this.placementTimer);
      this.placementTimer = window.setTimeout(() => this.placeProductFeatures(), 80);
    });
    const productRoot = document.querySelector('main-product, [data-section*="main-product"], #MainContent');
    if (productRoot) this.productObserver.observe(productRoot, { childList: true, subtree: true });

    this.initShopColor();
  }

  disconnectedCallback() {
    this.productObserver?.disconnect();
    this.colorAbortController?.abort();
    this.colorFeedAbortController?.abort();
    window.clearTimeout(this.placementTimer);
    window.clearTimeout(this.colorTimer);
  }

  getPicker() {
    return document.querySelector('.x-variant-picker.variant-selects-main, .x-variant-picker');
  }

  productMatchesLens() {
    const currentHandle = (this.dataset.currentProductHandle || '').trim().toLowerCase();
    const lensHandle = (this.dataset.lensProductHandle || '').trim().toLowerCase();
    const title = (document.querySelector('h1')?.textContent || '').trim().toLowerCase();
    return this.dataset.productMatch === 'true' ||
      (!!currentHandle && !!lensHandle && currentHandle === lensHandle) ||
      currentHandle.includes('witherspoon') ||
      title.includes('witherspoon');
  }

  placeProductFeatures() {
    const picker = this.getPicker();
    const fallback = document.querySelector('.x-block-buy-buttons, product-form');
    const inlineTarget = document.querySelector('[data-liora-lens-inline-target]');
    let anchor = picker;

    if (this.inlineButton && this.productMatchesLens()) {
      this.inlineButton.hidden = false;
      if (inlineTarget && this.inlineButton.parentElement !== inlineTarget) {
        inlineTarget.appendChild(this.inlineButton);
      } else if (!inlineTarget && picker && this.inlineButton.previousElementSibling !== picker) {
        picker.insertAdjacentElement('afterend', this.inlineButton);
      } else if (!inlineTarget && !picker && fallback) {
        fallback.insertAdjacentElement('beforebegin', this.inlineButton);
      }

      if (this.inlineButton.isConnected && !this.contains(this.inlineButton)) {
        this.classList.add('has-inline');
        anchor = inlineTarget || this.inlineButton;
      }
    }

    if (this.shopColor) {
      const shopColorAnchor = fallback || anchor;
      if (shopColorAnchor && this.shopColor.previousElementSibling !== shopColorAnchor) {
        shopColorAnchor.insertAdjacentElement('afterend', this.shopColor);
      }
    }
  }

  initShopColor() {
    if (!this.shopColor) return;
    this.shopColor.hidden = false;
    const update = () => {
      window.clearTimeout(this.colorTimer);
      this.colorTimer = window.setTimeout(() => this.loadSelectedColor(), 180);
    };

    // Listen at document level because Eurus may replace the picker markup.
    document.addEventListener('change', (event) => {
      if (event.target.closest?.('.x-variant-picker')) update();
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest?.('.x-variant-picker')) update();
    });
    document.addEventListener('eurus:product:variant-changed', update);
    document.addEventListener('eurus:variant:change', update);
    this.loadSelectedColor();
  }

  selectedColor() {
    const picker = this.getPicker();
    if (!picker) return '';
    const colorGroup = picker.querySelector('[data-option-name="color"], [data-option-name="colour"], [data-option-name*="color"]');
    if (!colorGroup) return '';
    const checked = colorGroup.querySelector('input:checked');
    if (checked) return checked.value || checked.getAttribute('data-value') || '';
    const select = colorGroup.querySelector('select');
    if (select) return select.value || '';
    const selected = colorGroup.querySelector('[aria-checked="true"], .selected, [data-selected="true"]');
    return selected?.getAttribute('data-value') || selected?.getAttribute('title') || selected?.textContent?.trim() || '';
  }

  normalizeColorKey(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  normalizeVendorKey(value) {
    const key = String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    return key === 'reneeofparis' ? 'reneofparis' : key;
  }

  async fetchColorFeedPage(collectionHandle, page, signal) {
    const root = window.Shopify?.routes?.root || '/';
    const feedUrl = new URL(`${root}collections/${encodeURIComponent(collectionHandle)}`, window.location.origin);
    feedUrl.searchParams.set('view', 'liora-color-feed');
    feedUrl.searchParams.set('page', String(page));
    feedUrl.searchParams.set('_liora_inventory', `${Date.now()}-${page}`);

    const response = await fetch(feedUrl.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store',
      signal
    });
    if (!response.ok) throw new Error(`Color inventory feed failed (${response.status})`);

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = doc.querySelector('[data-liora-color-feed]');
    const json = wrapper?.querySelector('[data-liora-color-feed-json]');
    if (!wrapper || !json) throw new Error('Color inventory feed marker missing');

    let records;
    try {
      records = JSON.parse(json.textContent || '[]');
    } catch (error) {
      throw new Error('Color inventory feed JSON is invalid');
    }

    return {
      records: Array.isArray(records) ? records : [],
      totalPages: Math.max(1, Number.parseInt(wrapper.dataset.totalPages || '1', 10) || 1)
    };
  }

  async loadColorInventoryFeed() {
    const collectionHandle = (this.shopColor?.dataset.colorCollectionHandle || '').trim();
    if (!collectionHandle) throw new Error('Color Explorer collection is not configured');

    if (this.colorFeed && this.colorFeedHandle === collectionHandle) return this.colorFeed;
    if (this.colorFeedPromise && this.colorFeedPromiseHandle === collectionHandle) return this.colorFeedPromise;

    this.colorFeedPromiseHandle = collectionHandle;
    this.colorFeedPromise = (async () => {
      const allRecords = [];
      let page = 1;
      let totalPages = 1;

      do {
        const payload = await this.fetchColorFeedPage(
          collectionHandle,
          page,
          this.colorFeedAbortController.signal
        );
        allRecords.push(...payload.records);
        totalPages = Math.min(payload.totalPages, 20);
        page += 1;
      } while (page <= totalPages);

      this.colorFeed = allRecords;
      this.colorFeedHandle = collectionHandle;
      return allRecords;
    })();

    try {
      return await this.colorFeedPromise;
    } catch (error) {
      this.colorFeedPromise = null;
      this.colorFeedPromiseHandle = '';
      throw error;
    }
  }

  createColorCard(record) {
    const article = document.createElement('article');
    article.className = 'liora-color-card';
    article.dataset.productId = String(record.productId || '');
    article.dataset.productHandle = String(record.handle || '');
    article.dataset.vendor = String(record.vendor || '');
    article.dataset.variantId = String(record.variantId || '');

    const link = document.createElement('a');
    link.className = 'liora-color-card__link';
    link.href = String(record.url || '#');

    const imageWrap = document.createElement('div');
    imageWrap.className = 'liora-color-card__image';
    if (record.image) {
      const image = document.createElement('img');
      image.src = String(record.image);
      image.alt = String(record.title || 'Matching style');
      image.loading = 'lazy';
      image.decoding = 'async';
      imageWrap.appendChild(image);
    }

    const title = document.createElement('h3');
    title.className = 'liora-color-card__title';
    title.textContent = String(record.title || '');

    const price = document.createElement('p');
    price.className = 'liora-color-card__price';
    price.textContent = String(record.price || '');

    const badge = document.createElement('span');
    badge.className = 'liora-color-card__badge';
    badge.textContent = String(record.color || '');

    link.append(imageWrap, title, price, badge);
    article.appendChild(link);
    return article;
  }

  matchingColorRecords(records, color) {
    const wantedColor = this.normalizeColorKey(color);
    const currentProductId = String(this.shopColor?.dataset.productId || '');
    const vendor = this.normalizeVendorKey(this.shopColor?.dataset.vendor || '');
    const seenProducts = new Set();

    return records
      .filter((record) => {
        if (this.normalizeColorKey(record.color) !== wantedColor) return false;
        if (String(record.productId || '') === currentProductId) return false;
        if (vendor && this.normalizeVendorKey(record.vendor || '') !== vendor) return false;

        const productKey = String(record.productId || record.handle || '');
        if (!productKey || seenProducts.has(productKey)) return false;
        seenProducts.add(productKey);
        return true;
      })
      .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
      .slice(0, 12);
  }

  async loadSelectedColor() {
    const color = this.selectedColor();
    const requestId = (this.colorRequestId || 0) + 1;
    this.colorRequestId = requestId;

    const name = this.shopColor.querySelector('[data-liora-color-name]');
    const status = this.shopColor.querySelector('[data-liora-color-status]');
    const results = this.shopColor.querySelector('[data-liora-color-results]');
    const collectionTitle = (this.shopColor.dataset.colorCollectionTitle || this.shopColor.dataset.vendor || '').trim();

    if (!color) {
      name.textContent = 'this color';
      status.hidden = false;
      status.textContent = 'Choose a color to explore matching styles.';
      results.innerHTML = '';
      return;
    }

    name.textContent = color;
    status.hidden = false;
    status.textContent = `Checking live ${collectionTitle || 'brand'} inventory…`;
    results.innerHTML = '';

    try {
      const inventoryRecords = await this.loadColorInventoryFeed();
      if (requestId !== this.colorRequestId) return;

      const matches = this.matchingColorRecords(inventoryRecords, color);
      matches.forEach((record) => results.appendChild(this.createColorCard(record)));

      if (matches.length) {
        status.hidden = true;
      } else {
        status.hidden = false;
        status.textContent = `No additional styles were found in the live inventory check for ${color}.`;
      }
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.colorRequestId) return;
      status.hidden = false;
      status.textContent = 'The live color inventory check is temporarily unavailable. Please refresh or contact Liora.';
    }
  }

  open(trigger) {
    this.lastTrigger = trigger;
    this.classList.add('is-open');
    this.bubble?.setAttribute('aria-expanded', 'true');
    this.inlineButton?.setAttribute('aria-expanded', 'true');
    this.panel?.setAttribute('aria-hidden', 'false');
    this.preview?.pause();
    this.inlinePreview?.pause();
    if (this.video) {
      this.video.currentTime = 0;
      this.video.play().catch(() => {});
    }
    this.closeButton?.focus();
  }

  close() {
    this.classList.remove('is-open');
    this.bubble?.setAttribute('aria-expanded', 'false');
    this.inlineButton?.setAttribute('aria-expanded', 'false');
    this.panel?.setAttribute('aria-hidden', 'true');
    if (this.video) {
      this.video.pause();
      this.video.muted = true;
    }
    this.preview?.play().catch(() => {});
    this.inlinePreview?.play().catch(() => {});
    this.updateSoundIcon();
    this.lastTrigger?.focus();
  }

  toggleSound() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.updateSoundIcon();
  }

  togglePlay() {
    if (!this.video) return;
    this.video.paused ? this.video.play().catch(() => {}) : this.video.pause();
    this.updatePlayIcon();
  }

  updateSoundIcon() {
    this.soundButton?.setAttribute('data-muted', this.video?.muted ? 'true' : 'false');
    this.soundButton?.setAttribute('aria-label', this.video?.muted ? 'Turn sound on' : 'Mute video');
  }

  updatePlayIcon() {
    this.playButton?.setAttribute('data-paused', this.video?.paused ? 'true' : 'false');
    this.playButton?.setAttribute('aria-label', this.video?.paused ? 'Play video' : 'Pause video');
  }
}

if (!customElements.get('liora-lens')) customElements.define('liora-lens', LioraLens);
